import {
  type GaugeMetricData,
  type GaugeMetricsMap,
} from '@/warranty/domain/entities/gauge-metric';
import { tailwindColorMap } from '@/warranty/view/components/tailwind.colors';

const dynamicMin = (m: GaugeMetricData): number =>
  m.yearToDate
    ? Math.min(
        90,
        Math.min(m.yearToDate, m.futurePrediction ?? m.yearToDate) - 5,
      )
    : 90;

// Original array of metrics for reference and data preparation
const dummyMetricsArray: GaugeMetricData[] = [
  {
    id: 'SOH',
    groupFullId: 'Country_Corner',
    longName: 'State of Health',
    state: 'active',
    yearToDate: 85,
    futurePrediction: 84,
    predictionWindowEnd: new Date('2024-03-31'),
    min: 0,
    max: 100,
    zones: [
      { from: 0, to: 91, color: tailwindColorMap.warning }, // orange
      { from: 91, to: 100, color: tailwindColorMap.ok }, // green
    ],
  },
  {
    id: 'RTE',
    groupFullId: 'Country_Corner',
    longName: 'Round Trip Efficiency',
    state: 'active',
    yearToDate: 85,
    futurePrediction: 84,
    min: 70,
    max: 100,
    zones: [
      { from: 0, to: 80, color: tailwindColorMap.warning },
      { from: 80, to: 100, color: tailwindColorMap.ok },
    ],
  },
  {
    id: 'TAG',
    groupFullId: 'Country_Corner',
    longName: 'Technical Availability Guarantee',
    state: 'active',
    yearToDate: 98,
    futurePrediction: 98,
    min: 90,
    max: 100,
    zones: [
      { from: 0, to: 97, color: tailwindColorMap.warning },
      { from: 97, to: 100, color: tailwindColorMap.ok },
    ],
  },
  {
    id: 'SOH',
    groupFullId: 'Smart_Hill',
    longName: 'State of Health',
    state: 'active',
    yearToDate: 90,
    futurePrediction: 89,
    min: 0,
    max: 100,
    zones: [
      { from: 0, to: 88, color: tailwindColorMap.warning }, // orange
      { from: 88, to: 100, color: tailwindColorMap.ok }, // green
    ],
  },
  {
    id: 'RTE',
    groupFullId: 'Smart_Hill',
    longName: 'Round Trip Efficiency',
    state: 'active',
    yearToDate: 85,
    futurePrediction: 84,
    min: 70,
    max: 100,
    zones: [
      { from: 0, to: 80, color: tailwindColorMap.warning },
      { from: 80, to: 100, color: tailwindColorMap.ok },
    ],
  },
  {
    id: 'TAG',
    groupFullId: 'Smart_Hill',
    longName: 'Technical Availability Guarantee',
    state: 'active',
    yearToDate: 98,
    futurePrediction: 98,
    min: dynamicMin({
      yearToDate: 99,
      futurePrediction: 98,
    } as GaugeMetricData),
    max: 100,
    zones: [
      { from: 0, to: 97, color: tailwindColorMap.warning },
      { from: 97, to: 100, color: tailwindColorMap.ok },
    ],
  },
  {
    id: 'SOH',
    groupFullId: 'Sunset_Plaza',
    longName: 'State of Health',
    state: 'active',
    yearToDate: 93,
    futurePrediction: 92,
    min: 0,
    max: 100,
    zones: [
      { from: 0, to: 91, color: tailwindColorMap.warning }, // orange
      { from: 91, to: 100, color: tailwindColorMap.ok }, // green
    ],
  },
  {
    id: 'RTE',
    groupFullId: 'Sunset_Plaza',
    longName: 'Round Trip Efficiency',
    state: 'active',
    yearToDate: 88,
    futurePrediction: 87,
    min: 70,
    max: 100,
    zones: [
      { from: 0, to: 80, color: tailwindColorMap.warning },
      { from: 80, to: 100, color: tailwindColorMap.ok },
    ],
  },
  {
    id: 'TAG',
    groupFullId: 'Sunset_Plaza',
    longName: 'Technical Availability Guarantee',
    state: 'active',
    yearToDate: 98,
    futurePrediction: 98,
    min: dynamicMin({
      yearToDate: 99,
      futurePrediction: 98,
    } as GaugeMetricData),
    max: 100,
    zones: [
      { from: 0, to: 97, color: tailwindColorMap.warning },
      { from: 97, to: 100, color: tailwindColorMap.ok },
    ],
  },
];

// Helper function to convert array of metrics to the map format
const createMetricsMap = (
  metrics: GaugeMetricData[],
  groupId: string,
): GaugeMetricsMap => {
  const filteredMetrics = metrics.filter((m) => m.groupFullId === groupId);

  return {
    SOH: filteredMetrics.find((m) => m.id === 'SOH')!,
    RTE: filteredMetrics.find((m) => m.id === 'RTE')!,
    TAG: filteredMetrics.find((m) => m.id === 'TAG')!,
  };
};

// Export the dummy data in the new map format
export const dummyPerformanceWarrantyStates = (
  groupId?: string,
): GaugeMetricsMap => {
  // If no groupId is provided, return the first group's metrics
  const targetGroupId = groupId || 'Country_Corner';
  return createMetricsMap(dummyMetricsArray, targetGroupId);
};
